// Global Instructions Rule Applied!
import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'

export interface SectionState<T> {
  /** null until the first successful load — callers may render an embedded fallback meanwhile. */
  data: T | null
  loading: boolean
  error: string | null
  /** Re-runs the load after a failure. */
  retry: () => void
}

/**
 * Lazy per-section fetch for a detail screen (the FundScreen investments pattern, made
 * reusable): component-local state with owned loading/error states and a Retry hook,
 * cancellation-safe across record changes. `load` runs whenever `key` changes (pass the
 * record id) and is skipped while `enabled` is false (e.g. nothing to resolve yet). The
 * latest `load` is kept in a ref so callers can pass an inline closure.
 */
export function useSectionData<T>(
  key: string | undefined | null,
  load: (key: string, accessToken: string | null) => Promise<T>,
  enabled: boolean = true
): SectionState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    if (!key || !enabled) return
    let cancelled = false
    setData(null)
    setLoading(true)
    setError(null)
    const token = getAccessToken(store.getState().auth)
    loadRef
      .current(key, token)
      .then((result) => {
        if (cancelled) return
        setData(result)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [key, enabled, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { data, loading, error, retry }
}
